"use client";

import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../lib/utils";
import { useKitseed, type KitseedDensity } from "../lib/provider";
import { Tooltip, useHasTooltip } from "./tooltip";

export const buttonVariants = cva(
  [
    "relative inline-flex shrink-0 select-none items-center justify-center gap-1.5",
    "rounded-control font-medium whitespace-nowrap outline-none",
    "transition-[background-color,border-color,color,box-shadow,opacity] duration-100 motion-reduce:transition-none",
    "focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-panel",
    "disabled:pointer-events-none disabled:opacity-40 aria-disabled:opacity-40 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-brand text-brand-fg shadow-xs hover:bg-brand-hover active:bg-brand-active",
        secondary:
          "border border-line-strong bg-control text-fg shadow-xs hover:bg-hover active:bg-active",
        subtle: "bg-hover text-fg hover:bg-active",
        ghost: "text-fg-muted hover:bg-hover hover:text-fg",
        danger: "bg-danger text-danger-fg shadow-xs hover:bg-danger-hover",
        "danger-ghost": "text-danger-text hover:bg-danger-subtle",
      },
      size: {
        sm: "h-7 px-2.5 text-[13px]",
        md: "h-8 px-3 text-[13px]",
        lg: "h-9 px-4 text-sm",
        icon: "size-8",
        "icon-sm": "size-7",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export type ButtonProps = useRender.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /** Keep the behavior and render composition while supplying a consumer-owned appearance. */
    unstyled?: boolean;
    density?: KitseedDensity;
    tooltip?: React.ReactNode;
    disabledReason?: string | undefined;
  };

export function Button({
  className,
  variant,
  size,
  render,
  density,
  tooltip,
  disabledReason,
  unstyled = false,
  ...props
}: ButtonProps) {
  const kitseed = useKitseed();
  const hasTooltip = useHasTooltip();
  const isDisabled =
    props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  const element = useRender({
    ...(render ? { render } : {}),
    defaultTagName: "button",
    props: {
      ...(render ? {} : { type: "button" as const }),
      ...props,
      "data-ui-control": size === "icon" || size === "icon-sm" ? "icon" : "button",
      ...(!unstyled ? { "data-density": density ?? kitseed.density } : {}),
      ...(disabledReason
        ? { "data-tooltip-disabled-reason": isDisabled ? disabledReason : undefined }
        : {}),
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
          event.preventDefault();
          return;
        }
        props.onClick?.(event);
      },
      className: cn(
        !unstyled && buttonVariants({ variant, size }),
        !unstyled && "max-sm:min-h-11 pointer-coarse:min-h-11 data-[density=comfortable]:min-h-11",
        !unstyled &&
          (size === "icon" || size === "icon-sm") &&
          "max-sm:min-w-11 pointer-coarse:min-w-11 data-[density=comfortable]:min-w-11",
        className,
      ),
    },
  });
  const content = isDisabled && disabledReason ? disabledReason : tooltip;
  return !hasTooltip && content ? <Tooltip content={content}>{element}</Tooltip> : element;
}
